// V2 Variant - Deep Psychological Onboarding Flow (Headway-style)
// Extended 30+ page flow focused on maximizing CVR through:
// - Deep motivation exploration
// - Pain point acknowledgment & validation
// - Identity building & transformation vision
// - Heavy social proof & statistics
// - Sunk cost / commitment building

// ============================================
// Import BASELINE pages (reused from baseline)
// ============================================
import BudgetRangePage from '../../baseline/onboarding/BudgetRangePage'
import ClutterLevelPage from '../../baseline/onboarding/ClutterLevelPage'
import ColorPreferencePage from '../../baseline/onboarding/ColorPreferencePage'
import CompletePage from '../../baseline/onboarding/CompletePage'
import ConditionalBudgetPage from '../../baseline/onboarding/ConditionalBudgetPage'
import ConditionalClutterPage from '../../baseline/onboarding/ConditionalClutterPage'
import HelpMethodPage from '../../baseline/onboarding/HelpMethodPage'
import HomeSizePage from '../../baseline/onboarding/HomeSizePage'
import MoodImpactInsightPage from '../../baseline/onboarding/MoodImpactInsightPage'
import MoodScalePage from '../../baseline/onboarding/MoodScalePage'
import PersonalityTypePage from '../../baseline/onboarding/PersonalityTypePage'
import PlantPreferencePage from '../../baseline/onboarding/PlantPreferencePage'
import PriorityPage from '../../baseline/onboarding/PriorityPage'
import SpaceVibePage from '../../baseline/onboarding/SpaceVibePage'
import StyleVibePage from '../../baseline/onboarding/StyleVibePage'

// ============================================
// Import V1 pages (reused psychological pages)
// ============================================
import InspirationSourcePage from '../../v1/onboarding/InspirationSourcePage'
import PastExperiencePage from '../../v1/onboarding/PastExperiencePage'
import TimeCommitmentPage from '../../v1/onboarding/TimeCommitmentPage'

// ============================================
// Import V2-ONLY pages (new deep psychology pages)
// ============================================
import ChangeCommitmentPage from './ChangeCommitmentPage'
import DeeperSpaceMotivationPage from './DeeperSpaceMotivationPage'
import DesignBlocksFeedbackPage from './DesignBlocksFeedbackPage'
import DesignIdentityPage from './DesignIdentityPage'
import DesignIdentityShiftPage from './DesignIdentityShiftPage'
import DesignInfluencesPage from './DesignInfluencesPage'
import DesignValuesFeedbackPage from './DesignValuesFeedbackPage'
import EmotionalGoalPage from './EmotionalGoalPage'
import FinalReadinessFeedbackPage from './FinalReadinessFeedbackPage'
import InvestmentMindsetFeedbackPage from './InvestmentMindsetFeedbackPage'
import LifestyleContextPage from './LifestyleContextPage'
import SpaceEmotionFeedbackPage from './SpaceEmotionFeedbackPage'
import SpaceFrustrationPage from './SpaceFrustrationPage'
import StyleConfidencePage from './StyleConfidencePage'
import SustainabilityValuePage from './SustainabilityValuePage'
import TransformationVisionFeedbackPage from './TransformationVisionFeedbackPage'

import navigateToNextOnboardingPageAction from '@actions/navigateToNextOnboardingPageAction'
import skipOnboardingLoader from '@app-studio-web/ui/loaders/skipOnboardingLoader'

import { Outlet } from 'react-router-dom'

/**
 * V2 Variant Routes - Deep Psychological Onboarding (Headway-style)
 *
 * Uses path: 'v2' so URLs are /onboarding/v2/page-name
 * First page is 'space-vibe' - same URL as baseline but with deep psychological extensions
 *
 * Total: ~34 pages designed to maximize CVR through:
 * 1. Investment through questions (sunk cost)
 * 2. Social proof & statistics at key moments
 * 3. Identity transformation & visualization
 * 4. Pain point acknowledgment & validation
 * 5. Commitment building before paywall
 */
const onboardingRoutes = [
  {
    path: 'v2',
    element: <Outlet />,
    loader: skipOnboardingLoader,
    children: [
      // ============================================
      // PHASE 1: HOOK & CONTEXT (4 pages)
      // Start with the same question, then go deeper
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <SpaceVibePage />,
        path: 'space-vibe',
      },
      // NEW: What would your ideal space truly mean to you?
      {
        action: navigateToNextOnboardingPageAction,
        element: <DeeperSpaceMotivationPage />,
        path: 'deeper-space-motivation',
      },
      // NEW: What frustrates you most about your current space?
      {
        action: navigateToNextOnboardingPageAction,
        element: <SpaceFrustrationPage />,
        path: 'space-frustration',
      },
      // NEW: Social proof feedback
      {
        action: navigateToNextOnboardingPageAction,
        element: <TransformationVisionFeedbackPage />,
        path: 'transformation-vision-feedback',
      },

      // ============================================
      // PHASE 2: LIFESTYLE ASSESSMENT (4 pages)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <HelpMethodPage />,
        path: 'help-method',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <HomeSizePage />,
        path: 'home-size',
      },
      // NEW: How do you spend most of your time at home?
      {
        action: navigateToNextOnboardingPageAction,
        element: <LifestyleContextPage />,
        path: 'lifestyle-context',
      },
      // NEW: How would you describe your design personality?
      {
        action: navigateToNextOnboardingPageAction,
        element: <DesignIdentityPage />,
        path: 'design-identity',
      },

      // ============================================
      // PHASE 3: PAIN POINTS & OBSTACLES (4 pages)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <ClutterLevelPage />,
        path: 'clutter-level',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ConditionalClutterPage />,
        path: 'conditional-clutter',
      },
      // Reuse from V1
      {
        action: navigateToNextOnboardingPageAction,
        element: <PastExperiencePage />,
        path: 'past-experience',
      },
      // NEW: Validation feedback on design blocks
      {
        action: navigateToNextOnboardingPageAction,
        element: <DesignBlocksFeedbackPage />,
        path: 'design-blocks-feedback',
      },

      // ============================================
      // PHASE 4: STYLE DEEP DIVE (5 pages)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <StyleVibePage />,
        path: 'style-vibe',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ColorPreferencePage />,
        path: 'color-preference',
      },
      // Reuse from V1
      {
        action: navigateToNextOnboardingPageAction,
        element: <InspirationSourcePage />,
        path: 'inspiration-source',
      },
      // NEW: What environments make you feel most alive?
      {
        action: navigateToNextOnboardingPageAction,
        element: <DesignInfluencesPage />,
        path: 'design-influences',
      },
      // NEW: How confident are you in making design decisions?
      {
        action: navigateToNextOnboardingPageAction,
        element: <StyleConfidencePage />,
        path: 'style-confidence',
      },

      // ============================================
      // PHASE 5: PRIORITIES & VALUES (4 pages)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <PriorityPage />,
        path: 'priority',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <PlantPreferencePage />,
        path: 'plant-preference',
      },
      // NEW: How important is eco-friendly design to you?
      {
        action: navigateToNextOnboardingPageAction,
        element: <SustainabilityValuePage />,
        path: 'sustainability-value',
      },
      // NEW: Statistics feedback on design values
      {
        action: navigateToNextOnboardingPageAction,
        element: <DesignValuesFeedbackPage />,
        path: 'design-values-feedback',
      },

      // ============================================
      // PHASE 6: INVESTMENT & COMMITMENT (4 pages)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <BudgetRangePage />,
        path: 'budget-range',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ConditionalBudgetPage />,
        path: 'conditional-budget',
      },
      // Reuse from V1
      {
        action: navigateToNextOnboardingPageAction,
        element: <TimeCommitmentPage />,
        path: 'time-commitment',
      },
      // NEW: Social proof on successful transformations
      {
        action: navigateToNextOnboardingPageAction,
        element: <InvestmentMindsetFeedbackPage />,
        path: 'investment-mindset-feedback',
      },

      // ============================================
      // PHASE 7: EMOTIONAL CONNECTION (4 pages)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <MoodScalePage />,
        path: 'mood-scale',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <MoodImpactInsightPage />,
        path: 'mood-impact-insight',
      },
      // NEW: What emotion do you want to feel when you walk into your space?
      {
        action: navigateToNextOnboardingPageAction,
        element: <EmotionalGoalPage />,
        path: 'emotional-goal',
      },
      // NEW: Statistics on home-emotion connection
      {
        action: navigateToNextOnboardingPageAction,
        element: <SpaceEmotionFeedbackPage />,
        path: 'space-emotion-feedback',
      },

      // ============================================
      // PHASE 8: IDENTITY & READINESS (4 pages)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <PersonalityTypePage />,
        path: 'personality-type',
      },
      // NEW: Can you see yourself living in your dream space?
      {
        action: navigateToNextOnboardingPageAction,
        element: <DesignIdentityShiftPage />,
        path: 'design-identity-shift',
      },
      // NEW: How committed are you to this transformation?
      {
        action: navigateToNextOnboardingPageAction,
        element: <ChangeCommitmentPage />,
        path: 'change-commitment',
      },
      // NEW: Personalized encouragement
      {
        action: navigateToNextOnboardingPageAction,
        element: <FinalReadinessFeedbackPage />,
        path: 'final-readiness-feedback',
      },

      // ============================================
      // PHASE 9: COMPLETION (1 page)
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <CompletePage />,
        path: 'complete',
      },
    ],
  },
]

export default onboardingRoutes
